// ---------- les préférences du joueur ----------
// Ce que la page Réglages règle (src/settings.ts). Gardées À PART de la progression, et pour la
// même raison que dans Codigrid (src/prefs.ts là-bas) : une préférence appartient à l'appareil où
// on l'a posée. Qui coupe les vibrations sur son téléphone dans une salle d'attente n'a rien dit
// de la tablette de la maison. Elles ne partent donc pas avec la sauvegarde (src/sync.ts).
//
// Le module lit, écrit ET applique : un appelant bascule un réglage, il n'a pas à savoir que les
// animations se coupent par un attribut sur <html> et les vibrations dans platform.ts.

const KEY = 'verbigrid:settings:v1';

export type KeyboardLayout = 'azerty' | 'abc';

export interface Settings {
  /** Une vibration brève quand un mot est trouvé (Android). */
  haptics: boolean;
  /** Animations allumées. Part du réglage système « réduire les animations », que le joueur
   *  peut contredire dans les deux sens : le système donne un point de départ, pas un verdict. */
  motion: boolean;
  /** La disposition du clavier du jeu. AZERTY d'office, celle des téléphones français ; ABC pour
   *  qui cherche ses lettres plus vite dans l'ordre de l'alphabet. */
  keyboard: KeyboardLayout;
}

const systemReduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const DEFAULTS: Settings = { haptics: true, motion: !systemReduced, keyboard: 'azerty' };

let current: Settings = { ...DEFAULTS };
try {
  const raw = localStorage.getItem(KEY);
  if (raw) {
    const p = JSON.parse(raw) as Partial<Settings>;
    current = {
      haptics: typeof p.haptics === 'boolean' ? p.haptics : DEFAULTS.haptics,
      motion: typeof p.motion === 'boolean' ? p.motion : DEFAULTS.motion,
      keyboard: p.keyboard === 'abc' ? 'abc' : 'azerty',
    };
  }
} catch {
  /* stockage refusé : les réglages d'office */
}

export const settings = (): Readonly<Settings> => current;

/** Change un réglage, l'enregistre et l'applique tout de suite. */
export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  current = { ...current, [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* stockage refusé : le réglage tient jusqu'à la fermeture */
  }
  applySettings();
}

/** Les animations coupées le sont par un attribut sur <html> (cf. `[data-motion='off']` dans
 *  src/css/style.css) : une seule règle arrête tout, décors compris, sans toucher aux écrans. */
export function applySettings(): void {
  if (current.motion) delete document.documentElement.dataset.motion;
  else document.documentElement.dataset.motion = 'off';
}
